import { ITaggableHcp } from "../@types/Staff";
import { IMentionConfig, IMentionItem } from "../components/RichTextEditor";

const MAX_DROPDOWN_ITEMS = 20;

function matchesSearchTerm(hcp: ITaggableHcp, searchTerm: string): boolean {
  if (!searchTerm) return true;
  const term = searchTerm.toLowerCase();
  return (
    hcp.firstName.toLowerCase().includes(term) ||
    hcp.lastName.toLowerCase().includes(term) ||
    hcp.email.toLowerCase().includes(term)
  );
}

/**
 * Builds the quill-mention config for the @-mention dropdown in note editors.
 * Takes getters (not the values themselves) so the source callback always reads
 * the latest data — Quill instantiates the editor once, capturing the config
 * in a closure, so we cannot rely on values at first render.
 *
 * Filtering is done in-memory against the list — server-side search would
 * mean a request per keystroke for what is typically a small list.
 *
 * @param getTaggableHcps  returns the HCPs that can be tagged for this patient
 * @param getAlreadyTaggedIds  returns the set of HCP IDs already mentioned in
 *   the note's current content — used to hide them from the dropdown so the
 *   same person cannot be tagged twice
 */
export function buildHcpMentionConfig(
  getTaggableHcps: () => ITaggableHcp[],
  getAlreadyTaggedIds: () => Set<number>
): IMentionConfig {
  return {
    source: (searchTerm, renderList) => {
      const list = getTaggableHcps();
      const alreadyTagged = getAlreadyTaggedIds();
      const matches: IMentionItem[] = list
        .filter((hcp) => !alreadyTagged.has(hcp.id))
        .filter((hcp) => matchesSearchTerm(hcp, searchTerm))
        .slice(0, MAX_DROPDOWN_ITEMS)
        .map((hcp) => ({
          id: String(hcp.id),
          value: hcp.email,
          firstName: hcp.firstName,
          lastName: hcp.lastName,
          email: hcp.email,
        }));
      renderList(matches, searchTerm);
    },
    renderItem: (item) => {
      // quill-mention v6 renders strings as innerText (escaped). To get styled
      // markup we have to build the DOM nodes ourselves — using textContent on
      // each part keeps it safe against XSS by construction.
      const firstName =
        typeof item.firstName === "string" ? item.firstName : "";
      const lastName = typeof item.lastName === "string" ? item.lastName : "";
      const fullName = `${firstName} ${lastName}`.trim() || item.value;

      const wrapper = document.createElement("span");
      wrapper.textContent = `${fullName} `;

      const email = document.createElement("span");
      email.style.color = "#727272";
      email.textContent = `(${item.value})`;
      wrapper.appendChild(email);

      return wrapper;
    },
  };
}
